import "core-js/stable";
import "regenerator-runtime/runtime";
import React from "react";
import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { AuthPage } from "./pages/AuthPage";
import { DibrettoPage } from "./pages/DibrettoPage";
import "./taskpane.css";
import * as Sentry from "@sentry/react";
import { useAuth } from "./hooks/useAuth";
import "./lib/i18n";
import { useTranslation } from "react-i18next";
import { msalConfig } from "./lib/msalConfig";

const msalInstance = new PublicClientApplication(msalConfig);

// Import Office mocks alleen in development
if (import.meta.env.DEV) {
  import("./mocks/office");
}

// Initialize Sentry alleen in productie
if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://9c91600cae7d0f571e4d43b0a86bf220@o4509547310612480.ingest.de.sentry.io/4509547312971856",
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
  });
}

const Taskpane: React.FC = () => {
  const [documentIsSecured, setDocumentIsSecured] = useState<boolean | null>(null);
  const { loginResponse, email, error, handleLogin, handleMicrosoftLogin, isLoading } = useAuth();
  const { t } = useTranslation();

  // Documentstatus checken (buiten render, na Office.onReady)
  useEffect(() => {
    const checkSecured = async () => {
      // In development, return false zodat we de app kunnen testen
      if (import.meta.env.DEV) {
        setDocumentIsSecured(false);
        return;
      }

      // In productie, gebruik echte Word API
      await Word.run(async (context) => {
        const controls = context.document.contentControls;
        controls.load("tag");
        await context.sync();
        const secured = controls.items.some((cc) => cc.tag === "beveiligd");
        setDocumentIsSecured(secured);
      });
    };
    checkSecured();
  }, []);

  if (documentIsSecured === null) {
    return <div>Loading...</div>;
  }

  if (documentIsSecured) {
    return (
      <div style={{ padding: "1rem", fontWeight: "bold" }}>{t("document.alreadyProcessed")}</div>
    );
  }

  return (
    <>
      {!loginResponse ? (
        <AuthPage
          onSubmit={handleLogin}
          onMicrosoftLogin={handleMicrosoftLogin}
          error={error}
          isLoading={isLoading}
        />
      ) : (
        <DibrettoPage {...loginResponse} email={email ?? ""} />
      )}
    </>
  );
};

export default Taskpane;

function renderApp(container: HTMLElement) {
  const root = createRoot(container);
  root.render(
    <MsalProvider instance={msalInstance}>
      <Taskpane />
    </MsalProvider>
  );
}

// Initialize app - gebruik mock in development, echte Office.onReady in productie
if (import.meta.env.DEV) {
  // In development, start direct zonder wachten op Office.onReady
  const container = document.getElementById("container");
  if (container) {
    renderApp(container);
  }
} else {
  // In productie, gebruik echte Office.onReady
  Office.onReady(() => {
    const container = document.getElementById("container");
    if (!container) return;
    renderApp(container);
  });
}
